import api from './api';

// Types
export interface Stone {
    type: string;
    count: number;
    weight: number;
    is_precious: boolean;
    price: string;
}

export interface Product {
    id: string;
    sku: string;
    name: string;
    description: string;
    gross_weight: number;
    net_weight: number;
    stone_weight: number;
    other_deductions: number;
    karat: number;
    gold_color: string;
    category: string;
    labor_fee_percent: number;
    labor_fee_fixed: string;
    profit_percent: number;
    tax_percent: number;
    stones: Stone[];
    image_urls: string[];
    stock: number;
    is_active: boolean;
    is_featured: boolean;
    created_at: string;
    // Calculated price (only if user is authenticated)
    calculated_price?: CalculatedPrice;
}

export interface CalculatedPrice {
    gold_weight: number;
    gold_price: string;
    gold_value: string;
    labor_fee: string;
    labor_fee_percent: number;
    stones_value: string;
    tax: string;
    tax_percent: number;
    profit: string;
    discount: string;
    total_price: string;
    stone_details?: StoneDetail[];
}

export interface StoneDetail {
    type: string;
    count: number;
    weight: number;
    is_precious: boolean;
    value: string;
}

export interface ProductFilters {
    category?: string;
    min_karat?: number;
    max_karat?: number;
    min_weight?: number;
    max_weight?: number;
    gold_color?: string;
    search?: string;
    is_featured?: boolean;
    page?: number;
    page_size?: number;
}

export interface ProductListResponse {
    products: Product[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
}

// Product categories
export const productCategories = [
    { value: 'ring', label: 'انگشتر', icon: '💍' },
    { value: 'necklace', label: 'گردنبند', icon: '📿' },
    { value: 'bracelet', label: 'دستبند', icon: '⌚' },
    { value: 'earring', label: 'گوشواره', icon: '✨' },
    { value: 'pendant', label: 'آویز', icon: '🔮' },
    { value: 'chain', label: 'زنجیر', icon: '⛓️' },
    { value: 'coin', label: 'سکه', icon: '🪙' },
    { value: 'bar', label: 'شمش', icon: '🧈' },
    { value: 'other', label: 'سایر', icon: '💎' },
];

export const stoneTypes = [
    { value: 'none', label: 'بدون سنگ' },
    { value: 'diamond', label: 'الماس' },
    { value: 'ruby', label: 'یاقوت سرخ' },
    { value: 'sapphire', label: 'یاقوت کبود' },
    { value: 'emerald', label: 'زمرد' },
    { value: 'turquoise', label: 'فیروزه' },
    { value: 'zircon', label: 'زیرکون' },
    { value: 'cubic_zirconia', label: 'نگین (اتمی)' },
    { value: 'other', label: 'سایر' },
];

export const getCategoryLabel = (value: string): string => {
    const category = productCategories.find(c => c.value === value);
    return category ? category.label : value;
};

export const getGoldColorLabel = (value: string): string => {
    switch (value?.toLowerCase()) {
        case 'yellow': return 'زرد';
        case 'white': return 'سفید';
        case 'rose': return 'رزگلد';
        default: return value || '-';
    }
};

// Product Service
export const productService = {
    /**
     * دریافت لیست محصولات با فیلتر
     */
    getProducts: async (filters: ProductFilters = {}): Promise<ProductListResponse> => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });

        const response = await api.get<ProductListResponse>(`/products?${params.toString()}`);
        return response.data;
    },

    /**
     * دریافت جزئیات یک محصول
     */
    getProduct: async (id: string): Promise<Product> => {
        const response = await api.get<Product>(`/products/${id}`);
        return response.data;
    },

    /**
     * دریافت محصولات ویژه
     */
    getFeaturedProducts: async (): Promise<Product[]> => {
        const response = await api.get<ProductListResponse>('/products?is_featured=true&page_size=8');
        return response.data.products;
    },

    /**
     * جستجوی محصولات
     */
    searchProducts: async (query: string): Promise<Product[]> => {
        const response = await api.get<ProductListResponse>(`/products?search=${encodeURIComponent(query)}`);
        return response.data.products;
    },

    /**
     * ایجاد محصول جدید
     */
    createProduct: async (data: Partial<Product>): Promise<Product> => {
        const response = await api.post<Product>('/admin/products', data);
        return response.data;
    },

    /**
     * ویرایش محصول
     */
    updateProduct: async (id: string, data: Partial<Product>): Promise<Product> => {
        const response = await api.put<Product>(`/admin/products/${id}`, data);
        return response.data;
    },

    /**
     * حذف محصول
     */
    deleteProduct: async (id: string): Promise<void> => {
        await api.delete(`/admin/products/${id}`);
    },

    /**
     * آپلود تصاویر محصول
     */
    uploadImages: async (id: string, files: FileList | File[]): Promise<Product> => {
        const formData = new FormData();
        Array.from(files).forEach((file) => {
            formData.append('images', file);
        });

        const response = await api.post<Product>(`/admin/products/${id}/images`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    /**
     * حذف تصویر محصول
     */
    removeImage: async (id: string, imageUrl: string): Promise<void> => {
        await api.delete(`/admin/products/${id}/images`, {
            params: { image_url: imageUrl }
        });
    },
};

export default productService;
